import { useEffect, useState, useCallback, useRef } from "react";
import isEmbroideryPremium from "./isEmbroideryPremium";
import { trackStartTrial } from "../analytics/metaPixel";

const POLL_INTERVALS = [2000, 3000, 4000, 5000, 6000, 8000, 10000];

function isReturningFromStripe() {
  const urlParams = new URLSearchParams(window.location.search);
  return (
    urlParams.has("checkout_success") ||
    document.referrer.includes("stripe.com") ||
    document.referrer.includes("checkout.stripe.com")
  );
}

const useEmbroideryPremiumStatus = (user) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollTimerRef = useRef(null);
  const pollAttemptRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollAttemptRef.current = 0;
  }, []);

  const check = useCallback(async () => {
    if (!user) {
      setStatus(false);
      setLoading(false);
      return false;
    }
    setLoading(true);
    const result = await isEmbroideryPremium();
    setStatus(result);
    setLoading(false);
    return result;
  }, [user]);

  const pollForStatus = useCallback(async () => {
    if (!user) return;
    const attempt = pollAttemptRef.current;
    if (attempt >= POLL_INTERVALS.length) {
      stopPolling();
      return;
    }
    const result = await isEmbroideryPremium();
    if (result) {
      setStatus(result);
      stopPolling();
      return;
    }
    pollAttemptRef.current = attempt + 1;
    const delay = POLL_INTERVALS[Math.min(attempt + 1, POLL_INTERVALS.length - 1)];
    pollTimerRef.current = setTimeout(pollForStatus, delay);
  }, [user, stopPolling]);

  const refresh = useCallback(async () => {
    stopPolling();
    const result = await check();
    if (!result) {
      pollAttemptRef.current = 0;
      pollTimerRef.current = setTimeout(pollForStatus, POLL_INTERVALS[0]);
    }
    return result;
  }, [check, pollForStatus, stopPolling]);

  useEffect(() => {
    if (user) {
      check();
    } else {
      setStatus(false);
      setLoading(false);
      stopPolling();
    }
  }, [user, check, stopPolling]);

  useEffect(() => {
    if (!user || !isReturningFromStripe()) return;
    const pendingType = localStorage.getItem("pendingCheckoutType") || "";
    const checkoutMode = localStorage.getItem("pendingCheckoutMode") || "";
    const t = setTimeout(() => {
      pollAttemptRef.current = 0;
      pollForStatus().then(() => {
        // No Purchase here: every EmbroideryTrace plan is a subscription, and the
        // webhook reports the Purchase once the first real charge clears.
        if (checkoutMode === "subscription_trial" && pendingType.startsWith("embroidery")) {
          trackStartTrial(pendingType);
        }
      });
    }, 2000);
    return () => clearTimeout(t);
  }, [user, pollForStatus]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  return [status, refresh, loading];
};

export default useEmbroideryPremiumStatus;
